/**
 * Copyright © 2026 Adnuntius AS.
 */

type PathMapping = {
  pattern: RegExp;
  destination: string;
};

type UiEnvironment = {
  uiiHostname: string;
  ruiOrigin: string;
};

const UI_ENVIRONMENTS: UiEnvironment[] = [
  {uiiHostname: 'localhost', ruiOrigin: 'http://localhost:5174'},
  {uiiHostname: 'dev.adnuntius.com', ruiOrigin: 'https://dev.rui.adnuntius.com'},
  {uiiHostname: 'admin.adnuntius.com', ruiOrigin: 'https://app.adnuntius.com'}
];

const PATH_MAPPINGS: PathMapping[] = [
  {pattern: /^\/advertisers(?:\/advertiser\/([^/]+))?\/?$/, destination: '/advertising/advertisers/$1'},
  {pattern: /^\/orders(?:\/order\/([^/]+))?\/?$/, destination: '/advertising/orders/$1'},
  {pattern: /^\/line-items(?:\/line-item\/([^/]+))?\/?$/, destination: '/advertising/lineitems/$1'},
  {pattern: /^\/creatives(?:\/creative\/([^/]+))?\/?$/, destination: '/advertising/creatives/$1'},
  {pattern: /^\/earnings-accounts(?:\/earnings-account\/([^/]+))?\/?$/, destination: '/publishing/earnings/$1'},
  {pattern: /^\/sites(?:\/site\/([^/]+))?\/?$/, destination: '/publishing/sites/$1'},
  {pattern: /^\/site-groups(?:\/site-group\/([^/]+))?\/?$/, destination: '/publishing/site-groups/$1'},
  {pattern: /^\/ad-units(?:\/ad-unit\/([^/]+))?\/?$/, destination: '/publishing/ad-units/$1'},
  {pattern: /^\/blocklists(?:\/blocklist\/([^/]+))?\/?$/, destination: '/publishing/blocklists/$1'},
  {pattern: /^\/rulesets(?:\/ruleset\/([^/]+))?\/?$/, destination: '/publishing/rulesets/$1'},
  {pattern: /^\/admin\/layouts(?:\/layout\/([^/]+))?\/?$/, destination: '/admin/layouts/$1'},
  {pattern: /^\/admin\/users(?:\/user\/([^/]+))?\/?$/, destination: '/admin/users/$1'},
  {pattern: /^\/admin\/products(?:\/product\/([^/]+))?\/?$/, destination: '/admin/products/$1'},
  {pattern: /^\/admin\/teams(?:\/team\/([^/]+))?\/?$/, destination: '/admin/teams/$1'},
  {pattern: /^\/admin\/custom-events(?:\/custom-event\/([^/]+))?\/?$/, destination: '/admin/custom-events/$1'},
  {pattern: /^\/political-entities(?:\/political-entity\/([^/]+))?\/?$/, destination: '/political/entities/$1'},
  {pattern: /^\/political-campaigns(?:\/political-campaign\/([^/]+))?\/?$/, destination: '/political/campaigns/$1'}
];

function mappedPath(pathname: string): string | undefined {
  for (const mapping of PATH_MAPPINGS) {
    const match = pathname.match(mapping.pattern);
    if (match) {
      return mapping.destination.replace('/$1', match[1] ? '/' + match[1] : '');
    }
  }
}

function moduleHome(currentApp: string): string {
  return currentApp === 'POLITICAL' ? '/political' : '/advertising';
}

export function ruiOriginForUiiHostname(hostname: string): string | undefined {
  const environment = UI_ENVIRONMENTS.find(candidate => candidate.uiiHostname === hostname.toLowerCase());
  return environment && environment.ruiOrigin;
}

export function translateUiiPathToRui(pathname: string, currentApp: string): string {
  const translated = mappedPath(pathname);
  if (translated) {
    return translated;
  }
  if (pathname === '/admin/network') {
    return '/admin/networks';
  }
  if (pathname === '/admin/user-profile') {
    return '/preferences';
  }
  if (pathname === '/reports') {
    return currentApp === 'POLITICAL' ? '/political' : '/advertising/reports';
  }
  if (pathname === '/elections' || pathname.startsWith('/elections/')) {
    return pathname.replace('/elections', '/political/elections');
  }
  if (pathname === '/ai') {
    return '/ai';
  }
  if (pathname === '/home' || pathname.startsWith('/welcome')) {
    return moduleHome(currentApp);
  }
  if (pathname.startsWith('/admin')) {
    return '/admin';
  }
  if (pathname.startsWith('/political')) {
    return '/political';
  }
  if (pathname.startsWith('/earnings-accounts')
    || pathname.startsWith('/sites')
    || pathname.startsWith('/ad-units')) {
    return '/publishing';
  }
  return moduleHome(currentApp);
}
